import {
  GenericDropdown,
  GenericDropdownContext,
  Icon,
  IconSVG,
  type ListItemProps,
  SVGColorKey,
} from "@studio3t/generic-components";
import type React from "react";
import { useMemo } from "react";
import styled from "styled-components";
import { FilterTrigger } from "@/components/TableFilters";
import type { Service } from "@/types/api";

interface ModelSelectorProps {
  services: Service[];
  activeName: string | null;
  onActivate: (name: string) => void;
  isActivating: boolean;
}

const PROVIDER_LABELS: Record<string, string> = {
  anthropic: "Anthropic",
  bedrock: "Bedrock",
  openai: "OpenAI",
};

function modelDisplayName(service: Service): string {
  const raw = service.model ?? service.model_id ?? service.name;
  const prefix = `${service.provider}.`;
  const cleaned = raw.startsWith(prefix) ? raw.slice(prefix.length) : raw;
  return cleaned
    .replace(/-\d{8}/, "")
    .replace(/-v\d+(?::\d+)?$/, "")
    .split("-")
    .map((part) =>
      /^\d+$/.test(part) ? part : part.charAt(0).toUpperCase() + part.slice(1),
    )
    .join(" ")
    .replace(/ (\d+) (\d+)/g, " $1.$2");
}

function providerLabel(provider: string): string {
  return PROVIDER_LABELS[provider] ?? provider;
}

function OptionLabel({ model, provider }: { model: string; provider: string }) {
  return (
    <OptionLabelRow>
      <ModelText>{model}</ModelText>
      <ProviderText>{provider}</ProviderText>
    </OptionLabelRow>
  );
}

function buildDropdownItems(
  services: Service[],
  activeName: string | null,
  onActivate: (name: string) => void,
): ListItemProps[][] {
  return [
    services.map((s) => ({
      id: s.name,
      label: modelDisplayName(s),
      customLabelRender: (
        <OptionLabel
          model={modelDisplayName(s)}
          provider={providerLabel(s.provider)}
        />
      ),
      type: "text" as const,
      value: s.name,
      selected: {
        value: s.name === activeName,
        icon: "checkmark" as const,
      },
      onClick: () => {
        if (s.name !== activeName) onActivate(s.name);
      },
    })),
  ];
}

// The trigger's text, "Model (Provider)" as the Figma footer shows it.
export function triggerLabel(
  activeService: Service | undefined,
  isActivating: boolean,
): string {
  if (isActivating) return "Activating…";
  if (!activeService) return "No model";
  return `${modelDisplayName(activeService)} (${providerLabel(
    activeService.provider,
  )})`;
}

// The Figma footer's Secondary small button with a chevron — the same
// trigger the table filters use.
function ModelTrigger({
  activeService,
  isActivating,
  open,
}: {
  activeService: Service | undefined;
  isActivating: boolean;
  open: boolean;
}) {
  return (
    <ModelButton
      type="button"
      $active={open}
      $busy={isActivating}
      data-testid="agent-model"
    >
      <TriggerText>{triggerLabel(activeService, isActivating)}</TriggerText>
      <IconSVG
        icon={Icon.BaseChevron}
        size={10}
        svgColorKey={SVGColorKey.semanticTextBody}
      />
    </ModelButton>
  );
}

export const ModelSelector: React.FC<ModelSelectorProps> = ({
  services,
  activeName,
  onActivate,
  isActivating,
}) => {
  const activeService = services.find((s) => s.name === activeName);

  const dropdownItems = useMemo(
    () => buildDropdownItems(services, activeName, onActivate),
    [services, activeName, onActivate],
  );

  if (services.length === 0) return null;

  return (
    <GenericDropdown
      dropdownItems={dropdownItems}
      width={{ strategy: "fixed", size: 280 }}
      placement="top-start"
      disabled={isActivating}
    >
      <GenericDropdownContext.Consumer>
        {({ open }) => (
          <ModelTrigger
            activeService={activeService}
            isActivating={isActivating}
            open={open}
          />
        )}
      </GenericDropdownContext.Consumer>
    </GenericDropdown>
  );
};

const ModelButton = styled(FilterTrigger)<{ $busy: boolean }>`
  min-width: 0;
  cursor: ${(p) => (p.$busy ? "wait" : "pointer")};
  opacity: ${(p) => (p.$busy ? 0.6 : 1)};
`;

const TriggerText = styled.span`
  max-width: 320px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const OptionLabelRow = styled.span`
  display: inline-flex;
  align-items: baseline;
  gap: 6px;
  min-width: 0;
`;

const ModelText = styled.span`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ProviderText = styled.span`
  font-size: 10px;
  color: ${(p) => p.theme.semanticTextPlaceholder};
  white-space: nowrap;
  flex-shrink: 0;
`;

export default ModelSelector;
